import { t } from '../i18n';
import { useState, useEffect, type FC } from 'react';
import {
  submitLessonTest,
  explainMistakesApi,
  startTestApi,
  spendCoinsApi,
  type Test,
  apiErrorMessage,
} from '../api/client';
import { useAppStore } from '../store/useAppStore';
import { useQueryClient } from '@tanstack/react-query';
import WebApp from '@twa-dev/sdk';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Play, RotateCcw, Sparkles, X } from 'lucide-react';
import { TypewriterText } from '../components/TypewriterText';
import { AiTypingIndicator } from '../components/AiTypingIndicator';
import { AnalysisVideo } from '../components/AnalysisVideo';
import {
  RetryWaitNote,
  useAnalysisWatched,
  useRetryWait,
} from '../components/RetryWait';
import { useConfirm } from '../components/ConfirmProvider';

interface QuizEngineProps {
  lessonId: string;
  tests: Test[];
  onCompleteSuccess: () => void;
  isAlreadyCompleted?: boolean;
  maxAttempts?: number;
  timeLimitSec?: number;
  currentAttempts?: number;
  /** Ustoz alohida bergan qo'shimcha urinishlar. */
  extraAttempts?: number;
  /** Tanga bilan urinish sotib olish narxi. 0 yoki undefined => o'chiq. */
  coinPriceExtraAttempt?: number;
  /** O'quvchining joriy balansi — tugmani ko'rsatish uchun. */
  coins?: number;
  onBought?: () => void;
  analysisVideoUrl?: string | null;
  analysisVideoUnavailable?: boolean;
  /** Yiqilgan urinishdan keyin keyingisi shu vaqtda ochiladi (ISO). */
  retryAfter?: string | null;
  /**
   * Qadamli darsda keyingi qadam: oʻtgandan keyin asosiy tugma unga
   * olib boradi ("Darslarga qaytish" emas). `ready` — server uni ochdi.
   */
  next?: { title: string; ready: boolean; onGo: () => void } | null;
}

interface QuizResult {
  score: number;
  isCompleted: boolean;
  message: string;
  resultsMap?: Record<string, boolean>;
  correctOptionsMap?: Record<string, number>;
  explanationsMap?: Record<string, string | null>;
  passingThreshold?: number;
}

/** Variant harfi: 0 -> A, 1 -> B ... */
const letter = (i: number) => String.fromCharCode(65 + i);

/** `options` DB'da JSON bo'lgani uchun massiv yoki satr bo'lishi mumkin. */
function toOptions(opts: unknown): string[] {
  if (Array.isArray(opts)) return opts as string[];
  if (typeof opts === 'string') {
    try {
      return JSON.parse(opts);
    } catch {
      return [opts];
    }
  }
  return [];
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export const QuizEngine: FC<QuizEngineProps> = ({
  lessonId,
  tests,
  onCompleteSuccess,
  isAlreadyCompleted = false,
  maxAttempts = 0,
  timeLimitSec = 0,
  currentAttempts = 0,
  extraAttempts = 0,
  coinPriceExtraAttempt = 0,
  coins = 0,
  onBought,
  analysisVideoUrl = null,
  analysisVideoUnavailable = false,
  retryAfter = null,
  next = null,
}) => {
  const { studentId } = useAppStore();
  const queryClient = useQueryClient();
  // Yiqilgan urinishdan keyin — kutish yoki tahlil videosi (RetryWait.tsx).
  const waitUntil = useRetryWait(retryAfter);
  const onAnalysisWatched = useAnalysisWatched(lessonId, !!waitUntil);
  const navigate = useNavigate();

  const confirm = useConfirm();

  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedIndices, setSelectedIndices] = useState<
    Record<string, number>
  >({});

  const [aiLoading, setAiLoading] = useState(false);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<QuizResult | null>(null);

  const [isStarted, setIsStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(
    timeLimitSec > 0 ? timeLimitSec : null,
  );
  const [isStarting, setIsStarting] = useState(false);
  const [forceRetake, setForceRetake] = useState(false);
  const [buying, setBuying] = useState(false);

  /*
   * Ustoz bergan qo'shimcha imkoniyat limitni NOLGA TUSHIRMAYDI, ustiga
   * qo'shadi. Shuning uchun amaldagi chegara = bazaviy + qo'shimcha.
   *
   * Ko'rsatishda esa sovg'a ALOHIDA chiqadi: 2/2 turgan dars +1 dan keyin
   * "Siz uchun berilgan imkoniyat: 0 / 1" bo'ladi — o'quvchi nima
   * berilganini aniq ko'radi.
   */
  /*
   * Tahlil videosi — test topshirilgandan keyin, oʻtgan-oʻtmaganidan
   * qatʼi nazar. Ilgari "allaqachon oʻtilgan" kartasida yoʻq edi: oʻtgan
   * oʻquvchi videoni faqat natija ekranida, bir marta koʻrardi, darsga
   * qaytganda esa umuman topolmasdi. Testdan OLDIN koʻrsatilmaydi —
   * bu yechim. Havola emas, sahifaning oʻzida (qarang AnalysisVideo).
   */
  const analysisBlock = (analysisVideoUrl || analysisVideoUnavailable) && (
    <div className="card-quiet">
      <span className="stat-label">
        <Play size={13} aria-hidden="true" /> {t('Tahlil videosi')}
      </span>
      <div style={{ marginTop: 'var(--space-2)' }}>
        <AnalysisVideo
          url={analysisVideoUrl}
          unavailable={analysisVideoUnavailable}
          onWatched={onAnalysisWatched}
        />
      </div>
    </div>
  );

  const effectiveLimit = maxAttempts + extraAttempts;
  const outOfAttempts = maxAttempts > 0 && currentAttempts >= effectiveLimit;
  const bonusUsed = Math.max(0, currentAttempts - maxAttempts);

  const handleSubmit = async () => {
    if (!studentId) return;
    setIsSubmitting(true);
    try {
      const payload = Object.keys(selectedIndices).map((testId) => ({
        testId,
        selectedOption: selectedIndices[testId] ?? 0,
      }));

      const res = await submitLessonTest(studentId, lessonId, payload);
      setResult(res as QuizResult);

      if (res.isCompleted) {
        try {
          WebApp.HapticFeedback.notificationOccurred('success');
        } catch {
          /* Telegramdan tashqarida */
        }
        // Faqat muvaffaqiyatda kerak — asosiy faylga qo'shilmasin.
        void import('canvas-confetti')
          .then(({ default: confetti }) =>
            confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 } }),
          )
          .catch(() => {
            /* bezak — yuklanmasa ham hech narsa buzilmaydi */
          });
        onCompleteSuccess();
        queryClient.invalidateQueries({ queryKey: ['course-progress'] });
      } else {
        try {
          WebApp.HapticFeedback.notificationOccurred('error');
        } catch {
          /* Telegramdan tashqarida */
        }
        // Server keyingi urinishga kutish qoʻydi — ekranda koʻrinsin.
        void queryClient.invalidateQueries({ queryKey: ['course-progress'] });
      }
    } catch (e: any) {
      toast.error(
        apiErrorMessage(e, t('Natijani saqlab boʻlmadi. Qaytadan urining.')),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (
      !isStarted ||
      timeLeft === null ||
      timeLeft <= 0 ||
      result ||
      isSubmitting
    )
      return;

    const id = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev !== null && prev <= 1) {
          clearInterval(id);
          void handleSubmit();
          return 0;
        }
        return prev ? prev - 1 : 0;
      });
    }, 1000);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStarted, timeLeft, result, isSubmitting]);

  /** Oʻtgandan keyingi asosiy tugma: keyingi qadam yoki darslar roʻyxati. */
  const nextButton = next ? (
    <button
      type="button"
      className="btn btn-primary btn-block"
      onClick={next.onGo}
      disabled={!next.ready}
    >
      {t('Keyingi qadam: {title}', { title: next.title })}
    </button>
  ) : (
    <button
      type="button"
      className="btn btn-primary btn-block"
      onClick={() => navigate('/')}
    >
      {t('Darslarga qaytish')}
    </button>
  );

  // ------------------------------------------------------------ test yo'q --

  if (!tests || tests.length === 0) {
    return (
      <div className="empty">
        <div className="empty-title">{t('Bu darsda test yoʻq')}</div>
        <p className="empty-text">
          {t(
            'Dars materialini oʻqib chiqing — keyingi dars avtomatik ochiladi.',
          )}
        </p>
      </div>
    );
  }

  // ------------------------------------------------------- allaqachon o'tgan --

  if (isAlreadyCompleted && !result && !forceRetake) {
    return (
      <div className="card ruled ruled-success">
        <span className="chip chip-success">
          <Check size={12} aria-hidden="true" /> {t('Oʻtilgan')}
        </span>
        <h2 style={{ margin: '10px 0 6px', fontSize: 'var(--text-lg)' }}>
          {t('Test topshirilgan')}
        </h2>
        <p
          className="secondary text-sm"
          style={{ marginBottom: 'var(--space-5)' }}
        >
          {next
            ? t('Bu qadamning testidan oʻtgansiz.')
            : t(
                'Bu darsning testidan muvaffaqiyatli oʻtgansiz va keyingi dars ochilgan.',
              )}
        </p>
        <div className="stack">
          {nextButton}
          {!outOfAttempts && (
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => setForceRetake(true)}
            >
              <RotateCcw size={16} aria-hidden="true" />
              {t('Qayta ishlash')}
            </button>
          )}
          {analysisBlock}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ boshlash --

  /*
   * Tanga evaziga urinish sotib olish.
   *
   * Balansni MIJOZDA tekshirmaymiz — server atomik ayiradi va yetmasa
   * aniq xato qaytaradi. Mijozdagi tekshiruv faqat tugmani ko'rsatish
   * uchun; haqiqat serverda.
   */
  const buyAttempt = async () => {
    const ok = await confirm({
      title: t('Qoʻshimcha urinish olinsinmi?'),
      body: t('{price} tanga sarflanadi. Balansingiz: {coins}.', {
        price: coinPriceExtraAttempt,
        coins,
      }),
      confirmText: t('Olish'),
    });
    if (!ok) return;

    setBuying(true);
    try {
      const r = await spendCoinsApi(lessonId, 'EXTRA_ATTEMPT');
      toast.success(t('Olindi. Qolgan tanga: {coins}', { coins: r.coins }));
      // Sarlavhadagi balansni ham yangilaymiz — aks holda u eski sonni
      // ko'rsatib turardi va o'quvchi tanga yechilmagandek o'ylardi.
      const s = useAppStore.getState();
      s.setGamification(r.coins, s.xp, s.streak);
      await queryClient.invalidateQueries({ queryKey: ['course-progress'] });
      onBought?.();
    } catch (e: any) {
      toast.error(apiErrorMessage(e, t('Sotib olib boʻlmadi.')));
    } finally {
      setBuying(false);
    }
  };

  // Ikki joyda (boshlash va natija ekrani) bir xil tugma.
  const buyLabel = buying
    ? t('Olinmoqda…')
    : coins < coinPriceExtraAttempt
      ? t('Tanga yetarli emas ({price} kerak, sizda {coins})', {
          price: coinPriceExtraAttempt,
          coins,
        })
      : t('{price} tangaga urinish olish', { price: coinPriceExtraAttempt });

  const startTest = async () => {
    if (outOfAttempts) {
      toast.error(t('Urinishlar soni tugagan.'));
      return;
    }
    setIsStarting(true);
    try {
      await startTestApi(lessonId);
      setIsStarted(true);
      // Urinish SARFLANDI — hisobni darhol yangilaymiz, aks holda ekranda
      // eski son qolib, keyingi bosishda server "urinishlar tugagan" deb
      // rad etardi va bu qarama-qarshilikdek ko'rinardi.
      void queryClient.invalidateQueries({ queryKey: ['course-progress'] });
    } catch (e: any) {
      toast.error(apiErrorMessage(e, t('Testni boshlab boʻlmadi.')));
    } finally {
      setIsStarting(false);
    }
  };

  if (!isStarted && !result) {
    return (
      <div className="card">
        <h2
          style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-4)' }}
        >
          {t('Nazorat testi')}
        </h2>

        <dl
          className="stack"
          style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-5)' }}
        >
          <div className="spread">
            <dt className="secondary text-sm">{t('Savollar')}</dt>
            <dd className="tabular text-sm">
              {t('{count} savol', { count: tests.length })}
            </dd>
          </div>
          {maxAttempts > 0 && (
            <div className="spread">
              <dt className="secondary text-sm">{t('Urinishlar')}</dt>
              <dd
                className={`tabular text-sm${
                  outOfAttempts && extraAttempts === 0 ? ' field-error' : ''
                }`}
              >
                {Math.min(currentAttempts, maxAttempts)} / {maxAttempts}
              </dd>
            </div>
          )}
          {extraAttempts > 0 && (
            <div className="spread">
              <dt className="text-sm" style={{ color: 'var(--accent)' }}>
                🎁 {t('Siz uchun berilgan imkoniyat')}
              </dt>
              <dd
                className={`tabular text-sm${outOfAttempts ? ' field-error' : ''}`}
              >
                {bonusUsed} / {extraAttempts}
              </dd>
            </div>
          )}
          {timeLimitSec > 0 && (
            <div className="spread">
              <dt className="secondary text-sm">{t('Vaqt')}</dt>
              <dd className="tabular text-sm">{formatTime(timeLimitSec)}</dd>
            </div>
          )}
        </dl>

        {!isAlreadyCompleted && (
          <p
            className="text-xs muted"
            style={{ marginBottom: 'var(--space-3)' }}
          >
            {t('XP birinchi urinish natijasidan beriladi.')}
          </p>
        )}

        {waitUntil && !outOfAttempts ? (
          <div className="stack">
            <RetryWaitNote until={waitUntil} hasAnalysis={!!analysisVideoUrl} />
            {analysisBlock}
          </div>
        ) : outOfAttempts ? (
          <>
            <div className="callout callout-danger">
              <X size={17} style={{ flex: 'none' }} aria-hidden="true" />
              <span>
                {t(
                  'Urinishlar tugagan. Qayta ochish uchun ustozingizga murojaat qiling.',
                )}
              </span>
            </div>
            {/* Tanga moduli yoqilgan bo'lsa o'quvchi o'zi ham hal qila oladi */}
            {coinPriceExtraAttempt > 0 && (
              <button
                type="button"
                className="btn btn-secondary btn-block"
                style={{ marginTop: 'var(--space-3)' }}
                onClick={buyAttempt}
                disabled={buying || coins < coinPriceExtraAttempt}
              >
                {buyLabel}
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={startTest}
            disabled={isStarting}
          >
            {isStarting ? t('Boshlanmoqda…') : t('Testni boshlash')}
          </button>
        )}
      </div>
    );
  }

  /*
   * "Qaytadan urinish" — BOSHLASH EKRANIGA qaytadi.
   *
   * Ilgari bu yerda faqat javoblar tozalanardi, `isStarted` esa `true`
   * qolardi. Natijada test darhol ochilardi, lekin serverda urinish YOPIQ
   * edi: `submitTest` topshirilgandan keyin `testStartedAt` ni tozalaydi.
   * Ikkinchi marta "Natijani ko'rish" bosilganda server 403 qaytarardi
   * ("urinish ochilmagan") va o'quvchi umumiy "Natijani saqlab boʻlmadi"
   * xabarini ko'rardi — ya'ni birinchi urinish ishlar, ikkinchisi hech
   * qachon ishlamasdi.
   *
   * `startTestApi` ni shu yerda jimgina chaqirmaymiz: u urinishni
   * SARFLAYDI. O'quvchi boshlash ekranida nechta urinish qolganini ko'rib,
   * o'zi bosishi to'g'riroq.
   */
  const retry = () => {
    setSelectedIndices({});
    setCurrentIdx(0);
    setResult(null);
    setAiExplanation(null);
    setShowReview(false);
    setIsStarted(false);
    setTimeLeft(timeLimitSec > 0 ? timeLimitSec : null);
  };

  const askAi = async () => {
    if (!studentId || !result) return;
    setAiLoading(true);
    try {
      const answers = tests.map((t) => ({
        testId: t.id,
        selectedOptionIndex: selectedIndices[t.id] ?? 0,
      }));
      const res = await explainMistakesApi(studentId, lessonId, answers);
      setAiExplanation(res.explanation);
    } catch {
      setAiExplanation(
        t(
          'Tahlilni olib boʻlmadi. Ustozingiz AI yordamchisini yoqmagan boʻlishi mumkin.',
        ),
      );
    } finally {
      setAiLoading(false);
    }
  };

  // -------------------------------------------------------------- tahlil --

  if (result && showReview) {
    return (
      <div className="card">
        <h2
          style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-4)' }}
        >
          {t('Test tahlili')}
        </h2>

        <div className="stack" style={{ gap: 'var(--space-5)' }}>
          {tests.map((test, index) => {
            const isCorrect = result.resultsMap?.[test.id];
            const chosen = selectedIndices[test.id];
            const correctIdx = result.correctOptionsMap?.[test.id];
            const explanation = result.explanationsMap?.[test.id];

            return (
              <div
                key={test.id}
                className={isCorrect ? 'ruled ruled-success' : 'ruled'}
              >
                <div
                  className="row"
                  style={{
                    alignItems: 'flex-start',
                    marginBottom: 'var(--space-3)',
                  }}
                >
                  <span className="lesson-num" aria-hidden="true">
                    {index + 1}
                  </span>
                  <p style={{ fontWeight: 600 }}>{test.question}</p>
                </div>

                <div className="stack" style={{ gap: 'var(--space-2)' }}>
                  {toOptions(test.options).map((opt, oIdx) => {
                    const picked = chosen === oIdx;
                    const isRight = oIdx === correctIdx;

                    let cls = 'opt';
                    if (isRight) cls += ' is-correct';
                    else if (picked) cls += ' is-wrong';

                    return (
                      <div key={oIdx} className={cls}>
                        <span className="opt-key" aria-hidden="true">
                          {letter(oIdx)}
                        </span>
                        <span style={{ flex: 1 }}>{opt}</span>
                        {isRight && (
                          <span className="chip chip-success">
                            {t('Toʻgʻri')}
                          </span>
                        )}
                        {picked && !isRight && (
                          <span className="chip chip-danger">{t('Siz')}</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {explanation && (
                  <div
                    className="callout callout-info"
                    style={{ marginTop: 'var(--space-3)' }}
                  >
                    <span>{explanation}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="btn btn-secondary btn-block"
          style={{ marginTop: 'var(--space-5)' }}
          onClick={() => setShowReview(false)}
        >
          {t('Natijaga qaytish')}
        </button>
      </div>
    );
  }

  // -------------------------------------------------------------- natija --

  if (result) {
    const passed = result.isCompleted;

    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`card ruled ${passed ? 'ruled-success' : ''}`}
      >
        <span className={`chip ${passed ? 'chip-success' : 'chip-warning'}`}>
          {passed ? t('Oʻtdingiz') : t('Oʻta olmadingiz')}
        </span>

        <div
          className="row"
          style={{ margin: 'var(--space-4) 0', gap: 'var(--space-5)' }}
        >
          <div className="stat">
            <span className="stat-value">{result.score}%</span>
            <span className="stat-label">{t('Natijangiz')}</span>
          </div>
          {result.passingThreshold !== undefined && (
            <div className="stat">
              <span className="stat-value secondary">
                {result.passingThreshold}%
              </span>
              <span className="stat-label">{t('Kerak edi')}</span>
            </div>
          )}
        </div>

        <p
          className="secondary text-sm"
          style={{ marginBottom: 'var(--space-5)' }}
        >
          {passed
            ? next
              ? t('Keyingi qadam ochildi.')
              : t('Keyingi dars ochildi.')
            : t('Xatolarni koʻrib chiqing va qayta urinib koʻring.')}
        </p>

        <div className="stack">
          {passed ? (
            <>
              {nextButton}
              {/* Urinish qolmagan bo'lsa taklif qilmaymiz — bosilganda
                  baribir server rad etardi. */}
              {!outOfAttempts && (
                <button
                  type="button"
                  className="btn btn-secondary btn-block"
                  onClick={retry}
                >
                  {t('Natijani yaxshilash')}
                </button>
              )}
            </>
          ) : (
            <>
              {outOfAttempts ? (
                <>
                  <div className="callout callout-warning">
                    {t(
                      'Urinishlar tugagan. Qayta ochish uchun ustozingizga murojaat qiling.',
                    )}
                  </div>
                  {coinPriceExtraAttempt > 0 && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-block"
                      style={{ marginTop: 'var(--space-3)' }}
                      onClick={buyAttempt}
                      disabled={buying || coins < coinPriceExtraAttempt}
                    >
                      {buyLabel}
                    </button>
                  )}
                </>
              ) : waitUntil ? (
                <RetryWaitNote
                  until={waitUntil}
                  hasAnalysis={!!analysisVideoUrl}
                />
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  onClick={retry}
                  disabled={aiLoading}
                >
                  <RotateCcw size={16} aria-hidden="true" />
                  {t('Qaytadan urinish')}
                </button>
              )}
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                {result.resultsMap && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => setShowReview(true)}
                  >
                    {t('Xatolarni koʻrish')}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  onClick={askAi}
                  disabled={aiLoading}
                >
                  <Sparkles size={15} aria-hidden="true" />
                  {aiLoading ? t('Tahlil…') : t('Nega xato?')}
                </button>
              </div>
            </>
          )}

          {analysisBlock}
        </div>

        {aiLoading && (
          <div className="row" style={{ marginTop: 'var(--space-4)' }}>
            <AiTypingIndicator />
            <span className="text-sm secondary">
              {t('Xatolar tahlil qilinmoqda')}
            </span>
          </div>
        )}

        {aiExplanation && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-quiet"
            style={{ marginTop: 'var(--space-4)' }}
          >
            <div className="prose text-sm">
              <TypewriterText text={aiExplanation} speed={15} />
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  }

  // ---------------------------------------------------------------- savol --

  const question = tests[currentIdx] || tests[0];
  const options = toOptions(question.options);
  const answered = selectedIndices[question.id] !== undefined;
  const allAnswered = Object.keys(selectedIndices).length === tests.length;
  const progress = Math.round(((currentIdx + 1) / tests.length) * 100);

  return (
    <div className="card">
      {timeLeft !== null && timeLeft > 0 && (
        <div
          className={`callout ${timeLeft < 60 ? 'callout-danger' : 'callout-info'}`}
          style={{ marginBottom: 'var(--space-4)', justifyContent: 'center' }}
          role="timer"
        >
          <Clock size={16} aria-hidden="true" />
          <span className="tabular">{formatTime(timeLeft)}</span>
        </div>
      )}

      <div className="spread" style={{ marginBottom: 6 }}>
        <span className="stat-label">
          {t('Savol {current} / {total}', {
            current: currentIdx + 1,
            total: tests.length,
          })}
        </span>
        <span className="text-xs muted tabular">{progress}%</span>
      </div>

      <div
        className="meter"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{ marginBottom: 'var(--space-5)' }}
      >
        <div className="meter-fill" style={{ width: `${progress}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.18 }}
        >
          <h2
            style={{
              fontSize: 'var(--text-lg)',
              lineHeight: 'var(--leading-snug)',
              marginBottom: 'var(--space-4)',
            }}
          >
            {question.question}
          </h2>

          <div
            className="stack"
            style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-5)' }}
            role="radiogroup"
            aria-label={t('Javob variantlari')}
          >
            {options.map((option, idx) => {
              const selected = selectedIndices[question.id] === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`opt${selected ? ' is-selected' : ''}`}
                  onClick={() => {
                    try {
                      WebApp.HapticFeedback.selectionChanged();
                    } catch {
                      /* Telegramdan tashqarida */
                    }
                    setSelectedIndices((prev) => ({
                      ...prev,
                      [question.id]: idx,
                    }));
                  }}
                >
                  <span className="opt-key" aria-hidden="true">
                    {letter(idx)}
                  </span>
                  <span style={{ flex: 1 }}>{option}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
          disabled={currentIdx === 0}
        >
          {t('Oldingi')}
        </button>

        {currentIdx < tests.length - 1 ? (
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={() => setCurrentIdx((i) => i + 1)}
            disabled={!answered}
          >
            {t('Keyingisi')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={handleSubmit}
            disabled={!allAnswered || isSubmitting}
          >
            {isSubmitting ? t('Tekshirilmoqda…') : t('Natijani koʻrish')}
          </button>
        )}
      </div>

      {!allAnswered && currentIdx === tests.length - 1 && (
        <p className="field-hint" style={{ marginTop: 'var(--space-2)' }}>
          {t('Barcha savollarga javob bering.')}
        </p>
      )}
    </div>
  );
};
